import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { OverviewBlock } from '@/config/overview-lenses';

export interface User {
  username: string;
  full_name: string;
  role: string;
  pages: string[];
  tabs?: Record<string, string[]> | null;
  capabilities?: string[] | null;
  template_id?: string | null;
  /** SPEC-489: true after an admin reset. Only the change-password screen is served. */
  must_change_password?: boolean;
  /** SPEC-515: the ONE view this login shows — its id, its label (role words only) and its
   *  Overview lens block. The Overview renders from `overview`. */
  view_id?: string | null;
  view_label?: string | null;
  overview?: OverviewBlock | null;
}

/** A saved view an admin can preview app-wide from the one view picker. */
export interface ViewTemplate {
  id: string;
  name: string;
  /** SPEC-515: the view's label (role words only); falls back to `name` for custom views. */
  label?: string;
  pages: string[] | null;
  tabs: Record<string, string[]> | null;
  capabilities: string[] | null;
  active: boolean;
  /** SPEC-515: the view's Overview lens block — previewing the view changes the Overview. */
  overview?: OverviewBlock | null;
}

/** SPEC-515: retired view ids an admin's remembered preview may still name. Mirrors
 *  backend/views.py LEGACY_VIEW_ALIASES. */
const LEGACY_VIEW_ALIASES: Record<string, string> = {
  demand_scheduler: 'demand_planner',
  sc_director: 'supply_chain',
  exec_result: 'executive',
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  loginWithSSOToken: (accessToken: string, user: User) => void;
  logout: () => void;
  /** SPEC-489: store a fresh session (change-password returns one; older tokens are dead). */
  applySession: (accessToken: string, user: User) => void;
  hasCapability: (cap: string) => boolean;
  /** Non-null while an admin previews a saved view template app-wide. */
  viewPreview: ViewTemplate | null;
  /** Templates available to preview (admins only; [] otherwise). */
  viewTemplates: ViewTemplate[];
  setViewPreview: (templateId: string | null) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: true,
  login: async () => {},
  loginWithSSOToken: () => {},
  logout: () => {},
  applySession: () => {},
  hasCapability: () => false,  // SPEC-255 F6: fail closed (default context, no provider)
  viewPreview: null,
  viewTemplates: [],
  setViewPreview: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen for forced logout from api.ts (401 + refresh failure)
  useEffect(() => {
    const handleForceLogout = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('suryaiq:logout', handleForceLogout);
    return () => window.removeEventListener('suryaiq:logout', handleForceLogout);
  }, []);

  // Check for stored token on mount
  useEffect(() => {
    const stored = localStorage.getItem('suryaiq_token');
    const storedUser = localStorage.getItem('suryaiq_user');
    if (stored && storedUser) {
      setToken(stored);
      setUser(JSON.parse(storedUser));
      // Verify token is still valid
      fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${stored}` },
      })
        .then((r) => {
          if (!r.ok) throw new Error('Token expired');
          return r.json();
        })
        .then((data) => {
          if (data.authenticated) {
            setUser(data);
          } else {
            throw new Error('Not authenticated');
          }
        })
        .catch(() => {
          localStorage.removeItem('suryaiq_token');
          localStorage.removeItem('suryaiq_user');
          setToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(err.detail || 'Login failed');
    }
    const data = await res.json();
    localStorage.setItem('suryaiq_token', data.access_token);
    localStorage.setItem('suryaiq_user', JSON.stringify(data.user));
    setToken(data.access_token);
    setUser(data.user);
  }, []);

  const applySession = useCallback((accessToken: string, sessionUser: User) => {
    localStorage.setItem('suryaiq_token', accessToken);
    localStorage.setItem('suryaiq_user', JSON.stringify(sessionUser));
    setToken(accessToken);
    setUser(sessionUser);
  }, []);
  const loginWithSSOToken = applySession;

  const logout = useCallback(() => {
    // Revoke refresh token server-side (best-effort)
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    localStorage.removeItem('suryaiq_token');
    localStorage.removeItem('suryaiq_user');
    setToken(null);
    setUser(null);
  }, []);

  // ── View preview (admin/super_admin) — the ONE view picker (SPEC-515) ─────
  // An admin can preview any saved view APP-WIDE: the served `user` below carries
  // the view's pages/tabs/capabilities AND its label + Overview block, so the
  // sidebar, tab gating, capability checks and the Overview all follow with no
  // per-consumer wiring — and because the choice is stored, it holds across
  // navigation and reloads. Client-side preview only: the server still authorises
  // as the real account.
  const isAdminRole = user?.role === 'admin' || user?.role === 'super_admin';
  const [previewId, setPreviewId] = useState<string | null>(
    () => localStorage.getItem('suryaiq_view_preview'),
  );
  const [viewTemplates, setViewTemplates] = useState<ViewTemplate[]>([]);

  useEffect(() => {
    if (!isAdminRole || !token) {
      setViewTemplates([]);
      return;
    }
    fetch('/api/auth/role-templates', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: ViewTemplate[]) => setViewTemplates(rows.filter((t) => t.active)))
      .catch(() => setViewTemplates([]));
  }, [isAdminRole, token]);

  const setViewPreview = useCallback((templateId: string | null) => {
    if (templateId) localStorage.setItem('suryaiq_view_preview', templateId);
    else localStorage.removeItem('suryaiq_view_preview');
    setPreviewId(templateId);
  }, []);

  // A remembered preview naming a retired id resolves to its successor (SPEC-515).
  const previewTarget = previewId ? (LEGACY_VIEW_ALIASES[previewId] || previewId) : null;
  const viewPreview =
    (isAdminRole && previewTarget && viewTemplates.find((t) => t.id === previewTarget)) || null;

  // The user everyone consumes: real identity, previewed surface area.
  const servedUser: User | null =
    user && viewPreview
      ? {
          ...user,
          pages: viewPreview.pages || user.pages,
          tabs: viewPreview.tabs ?? user.tabs,
          capabilities: viewPreview.capabilities ?? user.capabilities,
          view_id: viewPreview.id,
          view_label: viewPreview.label || viewPreview.name,
          overview: viewPreview.overview ?? null,
        }
      : user;

  const hasCapability = useCallback((cap: string): boolean => {
    const caps = (viewPreview ? viewPreview.capabilities : user?.capabilities) ?? null;
    // SPEC-255 F6: fail CLOSED — no capabilities present = deny, matching the backend
    // contract (SPEC-109). The backend always populates a capabilities array on login
    // (ROLE_DEFAULT_CAPABILITIES fallback), so a real logged-in user is never null here;
    // null only arises from a null-caps view preview or a stale cached user — both must
    // deny, not grant. (An empty array [] already denied — [].includes(cap) === false.)
    if (!caps) return false;
    return caps.includes(cap);
  }, [user, viewPreview]);

  return (
    <AuthContext.Provider value={{ user: servedUser, token, loading, login, loginWithSSOToken,
                                   logout, applySession, hasCapability, viewPreview, viewTemplates, setViewPreview }}>
      {children}
    </AuthContext.Provider>
  );
}
